import { useEffect, useRef, useState } from "react";
import "./scss/croem-widget.scss";

interface CroemPaymentProps {
  onTokenReceived: (
    token: string,
    cardOwnerName: string,
    cardOwnerLastName: string,
    cvv: string
  ) => void;
  onError: (error: string) => void;
  onCancel?: () => void;
  formError?: string;
  onActionCancel?: React.MouseEventHandler<HTMLButtonElement>;
  btnText?: string;
}

const CROEM_WIDGET_URL = process.env.REACT_APP_CROEM_WIDGET_URL ?? "https://gateway.merchantprocess.net/securecomponent/v2/UIComponent/CreditCard";
const CroemPayment = ({
  onTokenReceived,
  onError,
  onCancel,
  formError,
  onActionCancel,
  btnText = "Pagar",
}: CroemPaymentProps) => {

  const containerRef = useRef<HTMLDivElement>(null);

  const [cvv, setCvv] = useState("");
  const cvvRef = useRef("");

  const splitCardHolderName = (fullName: string) => {
    if (!fullName) return { name: "", lastName: "" };

    const parts = fullName.trim().split(" ");

    return {
      name: parts[0] || "",
      lastName: parts.slice(1).join(" ") || "",
    };
  };

  useEffect(() => {

    /* SUCCESS CALLBACK */

    (window as any).SaveCreditCard_SuccessCallback = (response: any) => {

      const token = response?.TokenDetails?.AccountToken;
      const cardHolderName = response?.TokenDetails?.CardHolderName;

      if (!token) {
        onError("Token no recibido");
        return;
      }

      if (!cvvRef.current) {
        onError("Ingresa el CVV de la tarjeta");
        return;
      }

      const { name, lastName } = splitCardHolderName(cardHolderName);

      onTokenReceived(
        token,
        name,
        lastName,
        cvvRef.current
      );
    };

    /* ERROR CALLBACK */

    (window as any).SaveCreditCard_FailureCallback = (response: any) => {
      onError(response?.ResponseDescription || "Error procesando tarjeta");
    };

    /* CANCEL CALLBACK */

    (window as any).SaveCreditCard_CancelCallback = () => {
      onCancel?.();
    };

    loadWidget();

    async function loadWidget() {
      try {

        const response = await fetch(CROEM_WIDGET_URL);

        const html = await response.text();

        if (!containerRef.current) return;

        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");

        const scripts = Array.from(doc.querySelectorAll("script"));

        doc.querySelectorAll("script").forEach((s) => s.remove());

        containerRef.current.innerHTML = doc.body.innerHTML;

        cleanWidgetUI();

        await loadScriptsSequentially(scripts);

      } catch (err) {

        onError("Error cargando widget");

      }
    }

    function cleanWidgetUI() {

      const container = containerRef.current;
      if (!container) return;

      const notification = container.querySelector(
        "#card-ui-component-notification-container"
      );

      const tokenResponse = container.querySelector(
        "#card-ui-component-txt-creditcard-response"
      );

      const closeButton = container.querySelector("#close-button");

      const confirmButton = container.querySelector(
        "#card-ui-component-btn-confirm"
      );

      const cancelButton = container.querySelector(
        "#card-ui-component-btn-cancel"
      );

      if (notification) (notification as HTMLElement).style.display = "none";
      if (tokenResponse) (tokenResponse as HTMLElement).style.display = "none";
      if (closeButton) (closeButton as HTMLElement).style.display = "none";
      if (confirmButton) (confirmButton as HTMLElement).style.display = "none";
      if (cancelButton) (cancelButton as HTMLElement).style.display = "none";

    }

    function loadScriptsSequentially(scripts: HTMLScriptElement[]) {

      return scripts.reduce((promise, script) => {

        return promise.then(() => loadScript(script));

      }, Promise.resolve());

    }

    function loadScript(script: HTMLScriptElement) {

      return new Promise<void>((resolve, reject) => {

        const newScript = document.createElement("script");

        if (script.src) {

          newScript.src = script.src;
          newScript.onload = () => resolve();
          newScript.onerror = reject;

        } else {

          newScript.text = script.innerHTML;
          resolve();

        }

        document.body.appendChild(newScript);

      });

    }

    return () => {

      delete (window as any).SaveCreditCard_SuccessCallback;
      delete (window as any).SaveCreditCard_FailureCallback;
      delete (window as any).SaveCreditCard_CancelCallback;

    };

  });

  return (
    <>
      <div className="w-full bg-white px-4">

        <div className="w-full py-4">
          <h1 className="text-[#212121] font-semibold text-xl">
            Datos de tarjeta
          </h1>
        </div>

        <div
          ref={containerRef}
          id="creditcard-container"
          style={{ width: "100%", minHeight: "300px" }}
        >
          <div style={{ textAlign: "center", padding: "40px" }}>
            Cargando formulario de pago...
          </div>
        </div>

        {/* CVV */}

        <div className="mt-4">

          <label className="text-[#212121] font-semibold text-sm">
            CVV2
          </label>

          <input
            type="password"
            inputMode="numeric"
            maxLength={4}
            value={cvv}
            onChange={(e) => {
              setCvv(e.target.value);
              cvvRef.current = e.target.value;
            }}
            placeholder="123"
            className="w-full mt-1 border border-grayForm rounded-md py-2 px-3"
          />

        </div>

        {formError && (
          <div
            style={{
              backgroundColor: "#ffebee",
              borderLeft: "4px solid #d32f2f",
              padding: "12px",
              marginTop: "16px",
              borderRadius: "6px",
              color: "#c62828",
              fontSize: "14px",
            }}
          >
            {formError}
          </div>
        )}

      </div>

      {/* BOTONES */}

      <div className="bg-white w-full">

        <div className="w-full grid grid-cols-2 gap-4 px-4 py-4">

          <button
            type="button"
            className="text-[#212121] border border-[#212121] rounded-lg py-3"
            onClick={onActionCancel}
          >
            Cancelar
          </button>

          <button
            type="button"
            className="bg-[#212121] py-3 text-white rounded-lg"
            onClick={() => {

              if (!cvvRef.current) {
                onError("Ingresa el CVV de la tarjeta");
                return;
              }

              const submit = document.getElementById(
                "card-ui-component-btn-confirm"
              ) as HTMLButtonElement;

              if (submit) submit.click();

            }}
          >
            {btnText}
          </button>

        </div>

      </div>
    </>
  );
};

export default CroemPayment;